// activeUploadStore.ts
import { useSyncExternalStore } from "react";

export type ActiveUploadState = {
  activeRequirementId: number | string | null;
  isUploadComplete: boolean;
  isError: boolean;
  completedAt: number;
};

const listeners = new Set<() => void>();
let state: ActiveUploadState = {
  activeRequirementId: null,
  isUploadComplete: false,
  isError: false,
  completedAt: 0
};

function emit() { for (const l of Array.from(listeners)) l(); }

// Optional: read current state outside React (e.g., in helpers)
export const getActiveUploadState = () => state;

export const activeUploadActions = {
  setActiveRequirement(id: number | string | null) {
    if (state.activeRequirementId === id) return;
    state = { ...state, activeRequirementId: id };
    emit();
  },

  clearActiveRequirement() {
    if (state.activeRequirementId === null) return;
    state = { ...state, isUploadComplete: false, activeRequirementId: null };
    emit();
  },

  setUploadComplete(done: boolean, error: boolean = false) {
    if (state.isUploadComplete === done) return;
    state = { ...state, isUploadComplete: done, isError: error, completedAt: Date.now() };
    emit();
  },

  startForRequirement(id: number | string) {
    state = { ...state, activeRequirementId: id, isUploadComplete: false };
    emit();
  },

  completeUpload() {
    if (state.isUploadComplete) return;
    state = { ...state, isUploadComplete: true };
    emit();
  },

  // One-shot flag: returns true if it was complete and resets to false
  consumeUploadComplete(): boolean {
    if (!state.isUploadComplete) return false;
    state = { ...state, isUploadComplete: false };
    emit();
    return true;
  },

  clear() {
    state = { activeRequirementId: null, isUploadComplete: false, isError: false, completedAt: 0 };
    emit();
  },
};

export function useActiveUploadStore<T>(
  selector: (s: ActiveUploadState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
) {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => selector(state),
    () => selector({ activeRequirementId: null, isUploadComplete: false, isError: false, completedAt: 0 }) // SSR fallback
  );
}
